import React from "react";
import { connect } from "react-redux";
import {
  getNotifs,
  markNotifAsRead,
  markAllAsRead,
  deleteNotif,
  deleteAllNotifs,
} from "../../actions/notifications/notifications";
import NotificationSubject from "../../enums/NotificationSubject";
import { clearAuthentication } from "../../util/AuthUtil";
import "./NotificationBell.css";

class NotificationBell extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showDropdown: false,
      badgeHidden: false,
    };
    this.containerRef = React.createRef();
  }

  componentDidMount() {
    // Only load notifications if user is authenticated
    if (this.isAuthenticated()) {
      this.loadNotifications();
      // Poll for new notifications every 30 seconds
      this.notificationInterval = setInterval(() => {
        if (this.isAuthenticated()) {
          this.loadNotifications();
        } else {
          // Stop polling if user is no longer authenticated
          if (this.notificationInterval) {
            clearInterval(this.notificationInterval);
            this.notificationInterval = null;
          }
        }
      }, 30000);
    }

    // Add click outside listener
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    if (this.notificationInterval) {
      clearInterval(this.notificationInterval);
    }
    // Remove click outside listener
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    // Réafficher la pastille si de nouvelles notifications non lues arrivent
    const prevUnreadCount = prevProps.unreadCount || prevProps.notifications?.unreadCount || 0;
    const currentUnreadCount = this.props.unreadCount || this.props.notifications?.unreadCount || 0;

    if (currentUnreadCount > prevUnreadCount && this.state.badgeHidden) {
      this.setState({ badgeHidden: false });
    }
  }

  handleClickOutside = (event) => {
    if (
      this.containerRef.current &&
      !this.containerRef.current.contains(event.target) &&
      this.state.showDropdown
    ) {
      this.setState({ showDropdown: false });
      // Marquer toutes les notifications comme lues quand on ferme l'encart
      if (this.isAuthenticated()) {
        this.props.dispatch(markAllAsRead());
      }
    }
  };

  isAuthenticated = () => {
    const token = sessionStorage.getItem("jwt");
    // Check if token exists, is not null/undefined, and has the proper JWT format
    if (
      !token ||
      token === "null" ||
      token === "undefined" ||
      token.trim() === ""
    ) {
      return false;
    }

    // Basic JWT format check (should have 3 parts separated by dots)
    const tokenParts = token.split(".");
    if (tokenParts.length !== 3) {
      this.clearAuthenticationWithPolling();
      return false;
    }

    // Check if each part is a valid base64 string and decode payload
    try {
      atob(tokenParts[0]); // header
      const payload = JSON.parse(atob(tokenParts[1])); // payload

      // Validate payload structure
      if (!payload || typeof payload !== "object") {
        throw new Error("Invalid JWT payload structure");
      }

      // Check if token is expired
      if (payload.exp && Date.now() >= payload.exp * 1000) {
        this.clearAuthenticationWithPolling();
        return false;
      }

      // Check essential fields (user info)
      if (!payload.userId && !payload.id && !payload.sub) {
        this.clearAuthenticationWithPolling();
        return false;
      }

      // signature doesn't need to be base64 decodable on client side
    } catch (e) {
      this.clearAuthenticationWithPolling();
      return false;
    }

    return true;
  };

  clearAuthenticationWithPolling = () => {
    // Stop polling before clearing authentication
    if (this.notificationInterval) {
      clearInterval(this.notificationInterval);
      this.notificationInterval = null;
    }

    // Use the shared authentication clearing utility
    clearAuthentication();
  };

  loadNotifications = () => {
    if (this.isAuthenticated()) {
      this.props.dispatch(getNotifs());
    }
  };

  toggleDropdown = () => {
    const isOpening = !this.state.showDropdown;
    const isClosing = !isOpening;

    this.setState({
      showDropdown: isOpening,
      badgeHidden: isOpening ? true : this.state.badgeHidden,
    });

    if (isOpening && this.isAuthenticated()) {
      this.loadNotifications();
    }

    // Marquer toutes les notifications comme lues quand on ferme l'encart
    if (isClosing && this.isAuthenticated()) {
      this.props.dispatch(markAllAsRead());
    }
  };

  handleNotificationClick = (notification) => {
    // Double check authentication before any action
    if (!this.isAuthenticated()) {
      return;
    }

    // Mark as read if not already read
    if (!notification.is_read) {
      this.props.dispatch(
        markNotifAsRead(notification._id, () => {
          // Success callback: proceed with navigation only if mark as read succeeds
          this.navigateToNotification(notification);
        }),
      );
    } else {
      // If already read, navigate directly
      this.navigateToNotification(notification);
    }

    this.setState({ showDropdown: false });
  };

  navigateToNotification = (notification) => {
    // Final authentication check before navigation
    if (!this.isAuthenticated()) {
      return;
    }

    // Navigate to action URL if provided
    if (notification.action_url) {
      window.location.hash = notification.action_url;
    }
  };

  handleMarkAllAsRead = () => {
    if (!this.isAuthenticated()) return;

    this.props.dispatch(
      markAllAsRead(() => {
        this.loadNotifications();
      }),
    );
  };

  handleDeleteAllNotifications = () => {
    if (!this.isAuthenticated()) return;

    this.props.dispatch(
      deleteAllNotifs(() => {
        this.loadNotifications();
      }),
    );
  };

  handleDeleteNotification = (e, notificationId) => {
    if (!this.isAuthenticated()) return;

    e.stopPropagation();
    this.props.dispatch(deleteNotif({ notifId: notificationId }));
  };

  formatNotificationMessage = (message) => {
    if (!message) return "";

    // Détecter si le message contient "Raison:" et formater en conséquence
    const reasonMatch = message.match(/(.*)\nRaison:\s*(.+)/s);
    if (reasonMatch) {
      const mainMessage = reasonMatch[1];
      const reason = reasonMatch[2];
      return (
        <>
          {mainMessage}
          <br />
          <strong>Raison: {reason}</strong>
        </>
      );
    }

    // Remplacer les sauts de ligne par des <br />
    return message.split("\n").map((line, index) => (
      <span key={index}>
        {line}
        {index < message.split("\n").length - 1 && <br />}
      </span>
    ));
  };

  getNotificationMessage = (notification) => {
    if (notification.message) {
      return this.formatNotificationMessage(notification.message);
    }

    // Fallback messages based on subject
    switch (notification.subject) {
      case NotificationSubject.RENTAL_CONFIRMATION:
        return "Nouvelle demande de location reçue - Cliquez pour valider";
      case NotificationSubject.RENTAL_ACCEPTED:
        return "Votre demande de location a été acceptée";
      case NotificationSubject.RENTAL_DATE_CHANGED_BY_CLIENT:
        return "Le client a modifié la date de location";
      case NotificationSubject.RENTAL_DATE_CHANGED_BY_ADMIN:
        return "La date de votre location a été modifiée";
      case NotificationSubject.RENTAL_CANCELLED:
        return "Une location a été annulée";
      default:
        return "Nouvelle notification";
    }
  };

  getNotificationIcon = (notification) => {
    // Si la notification est lue, on affiche l'icône de validation
    if (notification.is_read) {
      return "fa-check-circle";
    }

    // Sinon, on affiche l'icône appropriée selon le sujet
    switch (notification.subject) {
      case NotificationSubject.RENTAL_CONFIRMATION:
        return "fa-calendar-plus";
      case NotificationSubject.RENTAL_ACCEPTED:
        return "fa-thumbs-up"; // Icône d'approbation pour les acceptations
      case NotificationSubject.RENTAL_DATE_CHANGED_BY_CLIENT:
      case NotificationSubject.RENTAL_DATE_CHANGED_BY_ADMIN:
        return "fa-calendar-alt";
      case NotificationSubject.RENTAL_CANCELLED:
        return "fa-times-circle";
      default:
        return "fa-bell";
    }
  };

  formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  render() {
    // Don't render if user is not authenticated
    if (!this.isAuthenticated()) {
      return null;
    }

    const { notifications, unreadCount } = this.props;
    const notificationList = notifications.notifications || [];
    const actualUnreadCount = unreadCount || notifications.unreadCount || 0;
    const showBadge = actualUnreadCount > 0 && !this.state.badgeHidden;

    // Trier les notifications : non lues en premier, puis par date décroissante
    const sortedNotifications = [...notificationList].sort((a, b) => {
      // Non lues en premier
      if (!a.is_read && b.is_read) return -1;
      if (a.is_read && !b.is_read) return 1;
      // Puis par date décroissante
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    return (
      <div className="notification-bell-container" ref={this.containerRef}>
        <div className="notification-bell" onClick={this.toggleDropdown}>
          <i className="fas fa-bell"></i>
          {showBadge && (
            <span className="notification-badge">
              {actualUnreadCount > 99 ? "99+" : actualUnreadCount}
            </span>
          )}
        </div>

        {this.state.showDropdown && (
          <div className="notification-dropdown">
            <div className="notification-header">
              <h6>Notifications</h6>
              {sortedNotifications.length > 0 && (
                <div
                  style={{ display: "flex", gap: "6px", alignItems: "center" }}
                >
                  <button
                    onClick={this.handleMarkAllAsRead}
                    className="notif-action-btn mark-read"
                    title="Tout marquer comme lu"
                  >
                    <i className="fas fa-check-double"></i>
                  </button>
                  <button
                    onClick={this.handleDeleteAllNotifications}
                    className="notif-action-btn delete-all"
                    title="Tout supprimer"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              )}
            </div>

            <div className="notification-list">
              {sortedNotifications.length === 0 ? (
                <div className="no-notifications">
                  <i className="fas fa-bell-slash"></i>
                  <p>Aucune notification</p>
                </div>
              ) : (
                sortedNotifications.map((notification, index) => (
                  <div
                    key={notification._id || index}
                    className={`notification-item ${
                      !notification.is_read ? "unread" : ""
                    }`}
                    onClick={() => this.handleNotificationClick(notification)}
                  >
                    <div className="notification-icon">
                      <i
                        className={`fas ${this.getNotificationIcon(
                          notification,
                        )}`}
                      ></i>
                    </div>
                    <div className="notification-content">
                      <p className="notification-message">
                        {this.getNotificationMessage(notification)}
                      </p>
                      <small className="notification-date">
                        {this.formatDate(notification.createdAt)}
                      </small>
                    </div>
                    <button
                      className="delete-notification"
                      onClick={(e) =>
                        this.handleDeleteNotification(e, notification._id)
                      }
                      title="Supprimer"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  notifications: state.notifications,
  unreadCount: state.notifications.unreadCount,
});

export default connect(mapStateToProps)(NotificationBell);
